/**
 * Local SQLite adapters for asset versions.
 * Mirrors the Convex api.assetVersions.* interface.
 */
import { localDb, newId, now } from "@/lib/localDb";
import { invalidate } from "./queryCache";
import { useLocalQuery, useLocalGet } from "./useLocalQuery";

// ─── Types ────────────────────────────────────────────────────────────────────

export type AssetEntityType = "panorama" | "icon" | "image" | "video" | "download";

export interface LocalAssetVersion {
  _id: string;
  project_id: string;
  user_id?: string;
  entity_type: AssetEntityType;
  entity_id: string;
  version_number: number;
  is_current: boolean;
  label?: string;
  file_url?: string;
  private_file_url?: string;
  storage_id?: string;
  file_hash?: string;
  file_size?: number;
  width?: number;
  height?: number;
  original_filename?: string;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useVersionsByEntity(
  entityType: AssetEntityType,
  entityId: string | undefined,
  craftspaceId: string | undefined,
): LocalAssetVersion[] | undefined {
  return useLocalQuery<LocalAssetVersion>(
    `SELECT id as _id, id, project_id, user_id, entity_type, entity_id,
       version_number, is_current, label,
       file_url, private_file_url, storage_id,
       file_hash, file_size, width, height,
       original_filename, metadata,
       created_at, updated_at
     FROM asset_versions
     WHERE entity_type = ? AND entity_id = ?
     ORDER BY version_number DESC`,
    [entityType, entityId ?? ""],
    ["asset_versions"],
    ["metadata"],
    craftspaceId,
  );
}

export function useCurrentVersion(
  entityType: AssetEntityType,
  entityId: string | undefined,
  craftspaceId: string | undefined,
): LocalAssetVersion | null | undefined {
  return useLocalGet<LocalAssetVersion>(
    `SELECT id as _id, id, project_id, user_id, entity_type, entity_id,
       version_number, is_current, label,
       file_url, private_file_url, storage_id,
       file_hash, file_size, width, height,
       original_filename, metadata,
       created_at, updated_at
     FROM asset_versions
     WHERE entity_type = ? AND entity_id = ? AND is_current = 1
     LIMIT 1`,
    [entityType, entityId ?? ""],
    ["asset_versions"],
    ["metadata"],
    craftspaceId,
  );
}

// ─── Non-hook queries ─────────────────────────────────────────────────────────

export async function findByHash(
  fileHash: string,
  projectId: string,
  craftspaceId: string,
): Promise<LocalAssetVersion | undefined> {
  return localDb(craftspaceId).get<LocalAssetVersion>(
    `SELECT id as _id, id, project_id, user_id, entity_type, entity_id,
       version_number, is_current, label,
       file_url, private_file_url, storage_id,
       file_hash, file_size, width, height,
       original_filename, metadata,
       created_at, updated_at
     FROM asset_versions
     WHERE file_hash = ? AND project_id = ?
     LIMIT 1`,
    [fileHash, projectId],
  );
}

export async function getNextVersionNumber(
  entityType: AssetEntityType,
  entityId: string,
  craftspaceId: string,
): Promise<number> {
  const row = await localDb(craftspaceId).get<{ max_version: number | null }>(
    `SELECT MAX(version_number) as max_version FROM asset_versions
     WHERE entity_type = ? AND entity_id = ?`,
    [entityType, entityId],
  );
  return (row?.max_version ?? 0) + 1;
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export async function createVersion(params: {
  craftspace_id: string;
  project_id: string;
  entity_type: AssetEntityType;
  entity_id: string;
  label?: string;
  file_url?: string;
  private_file_url?: string;
  storage_id?: string;
  file_hash?: string;
  file_size?: number;
  width?: number;
  height?: number;
  original_filename?: string;
  metadata?: Record<string, unknown>;
}): Promise<string> {
  const id = newId();
  const ts = now();

  const versionNumber = await getNextVersionNumber(params.entity_type, params.entity_id, params.craftspace_id);

  // Use a transaction: unset current on previous, insert new as current
  const operations: Array<{ sql: string; params?: unknown[] }> = [
    {
      sql: `UPDATE asset_versions SET is_current = 0, updated_at = ?
            WHERE entity_type = ? AND entity_id = ? AND is_current = 1`,
      params: [ts, params.entity_type, params.entity_id],
    },
    {
      sql: `INSERT INTO asset_versions (id, project_id, user_id, entity_type, entity_id,
              version_number, is_current, label,
              file_url, private_file_url, storage_id,
              file_hash, file_size, width, height,
              original_filename, metadata,
              created_at, updated_at)
            VALUES (?, ?, ?, ?, ?, ?, 1, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      params: [
        id,
        params.project_id,
        null, // user_id not available in local mode
        params.entity_type,
        params.entity_id,
        versionNumber,
        params.label ?? null,
        params.file_url ?? null,
        params.private_file_url ?? null,
        params.storage_id ?? null,
        params.file_hash ?? null,
        params.file_size ?? null,
        params.width ?? null,
        params.height ?? null,
        params.original_filename ?? null,
        params.metadata ? JSON.stringify(params.metadata) : null,
        ts,
        ts,
      ],
    },
  ];

  await localDb(params.craftspace_id).transaction(operations);
  invalidate("asset_versions");
  return id;
}

export async function revertToVersion(versionId: string, craftspaceId: string): Promise<void> {
  const version = await localDb(craftspaceId).get<LocalAssetVersion>(
    `SELECT id as _id, entity_type, entity_id, file_url, private_file_url,
       file_size, width, height, storage_id
     FROM asset_versions WHERE id = ?`,
    [versionId],
  );

  if (!version) throw new Error("Version not found");

  const ts = now();

  const operations: Array<{ sql: string; params?: unknown[] }> = [
    // Unset current on all versions for this entity
    {
      sql: `UPDATE asset_versions SET is_current = 0, updated_at = ?
            WHERE entity_type = ? AND entity_id = ? AND is_current = 1`,
      params: [ts, version.entity_type, version.entity_id],
    },
    // Set target version as current
    {
      sql: `UPDATE asset_versions SET is_current = 1, updated_at = ? WHERE id = ?`,
      params: [ts, versionId],
    },
  ];

  // Update parent entity with this version's file data
  if (version.entity_type === "panorama") {
    operations.push({
      sql: `UPDATE panoramas SET
              storage_path = COALESCE(?, storage_path),
              width = COALESCE(?, width),
              height = COALESCE(?, height),
              updated_at = ?
            WHERE id = ?`,
      params: [
        version.private_file_url ?? version.file_url ?? null,
        version.width ?? null,
        version.height ?? null,
        ts,
        version.entity_id,
      ],
    });
  }

  await localDb(craftspaceId).transaction(operations);
  invalidate("asset_versions", "panoramas");
}

export async function removeVersion(versionId: string, craftspaceId: string): Promise<void> {
  const version = await localDb(craftspaceId).get<{ is_current: number; entity_type: string; entity_id: string }>(
    `SELECT is_current, entity_type, entity_id FROM asset_versions WHERE id = ?`,
    [versionId],
  );

  if (!version) throw new Error("Version not found");

  if (version.is_current) {
    // Find another version to promote
    const other = await localDb(craftspaceId).get<{ id: string }>(
      `SELECT id FROM asset_versions
       WHERE entity_type = ? AND entity_id = ? AND id != ?
       ORDER BY version_number DESC LIMIT 1`,
      [version.entity_type, version.entity_id, versionId],
    );

    if (!other) throw new Error("Cannot delete the only version");

    const ts = now();
    await localDb(craftspaceId).transaction([
      { sql: `UPDATE asset_versions SET is_current = 1, updated_at = ? WHERE id = ?`, params: [ts, other.id] },
      { sql: `DELETE FROM asset_versions WHERE id = ?`, params: [versionId] },
    ]);
  } else {
    await localDb(craftspaceId).run(`DELETE FROM asset_versions WHERE id = ?`, [versionId]);
  }

  invalidate("asset_versions");
}
